import { Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCubeSend, IconExclamationCircle } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import Button from '@xver/components/Atoms/Button/Button';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { useActiveProject } from '@xver/hooks/useActiveProject';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

export const PREREQUISITE_QUERY_KEY = ['apis.v1.containers.prerequisite'];

interface ActivationGuardProps extends PropsWithChildren {
    title?: string;
    description?: string;
    blurb?: string;
    activateLabel?: string;
    activatingLabel?: string;
    /**
     * Check the caller's own Harbor account (the registry is personal) instead of the active
     * project owner's, which gates containers.
     */
    personal?: boolean;
}

export default function ActivationGuard({
    children,
    title,
    description,
    blurb,
    activateLabel,
    activatingLabel,
    personal = false,
}: ActivationGuardProps) {
    const { t } = useTranslation('containerEngine');
    const queryClient = useQueryClient();
    const { project, activeProjectId, isResolved } = useActiveProject();

    const { data, isPending, isError, refetch } = useQuery({
        queryKey: [...PREREQUISITE_QUERY_KEY, personal ? 'personal' : activeProjectId],
        queryFn: () => Endpoints.v1.Containers.getPrerequisite({ personal }),
        staleTime: 30_000,
        enabled: personal || isResolved,
    });

    const activateMutation = useMutation({
        mutationFn: Endpoints.v1.Containers.activate,
        onSuccess: async () => {
            notifications.show({
                title: t('activation.successTitle', 'Container Engine activated'),
                message: t('activation.successMessage', 'Your container engine is ready to use.'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await queryClient.invalidateQueries({ queryKey: PREREQUISITE_QUERY_KEY });
        },
        onError: (err: { message?: string }) => {
            notifications.show({
                title: t('activation.errorTitle', 'Activation failed'),
                message: err?.message ?? t('activation.errorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    if (isPending) {
        return (
            <div className="w-full p-12 flex justify-center">
                <Loader size={24} color="var(--fg)" />
            </div>
        );
    }

    if (isError) {
        return (
            <DialogBox
                label={t('activation.checkErrorTitle', 'Unable to check activation status')}
                description={t(
                    'activation.checkErrorDescription',
                    'We could not verify whether your account is activated.'
                )}
                classNames="p-6 gap-4 items-start"
            >
                <div className="flex flex-col items-start gap-2">
                    <div className="flex items-center gap-2 text-fg/80">
                        <IconExclamationCircle size={20} />
                        <span className="text-base font-medium">
                            {t('activation.checkErrorBlurb', 'Please try again.')}
                        </span>
                    </div>
                </div>
                <Button onClick={() => refetch()}>{t('activation.retry', 'Retry')}</Button>
            </DialogBox>
        );
    }

    if (data && data.exists === false && data.isOwner === false) {
        return (
            <DialogBox
                label={t('projects:activation.ownerTitle')}
                description={t(
                    'activation.description',
                    'Activate Container Engine to deploy containerised services and use the Artifact Registry.'
                )}
                classNames="p-6 gap-4 items-start"
            >
                <div className="flex items-start gap-2 text-fg/80">
                    <IconCubeSend size={24} className="shrink-0" aria-hidden="true" />
                    <p className="text-base font-medium max-w-2xl">
                        {t('projects:activation.ownerMessage', {
                            owner: project?.owner.name ?? '',
                        })}
                    </p>
                </div>
            </DialogBox>
        );
    }

    if (data && data.exists === false) {
        return (
            <DialogBox
                label={title ?? t('activation.title', 'Container Engine not yet activated')}
                description={
                    description ??
                    t(
                        'activation.description',
                        'Activate Container Engine to deploy containerised services and use the Artifact Registry.'
                    )
                }
                classNames="p-6 gap-4 items-start"
            >
                <div className="flex flex-col items-start gap-2">
                    <div className="flex items-center gap-2 text-fg/80">
                        <IconCubeSend size={24} />
                        <span className="text-base font-medium">
                            {t(
                                'activation.notActivated',
                                'You have not activated Container Engine'
                            )}
                        </span>
                    </div>
                    <p className="text-sm text-fg/68 max-w-2xl">
                        {blurb ??
                            t(
                                'activation.blurb',
                                'Activating provisions a Harbor account for storing container images. This is required before you can deploy containers or push artifacts.'
                            )}
                    </p>
                </div>
                <Button
                    onClick={() => activateMutation.mutate()}
                    disabled={activateMutation.isPending}
                >
                    {activateMutation.isPending
                        ? (activatingLabel ?? t('activation.activating', 'Activating...'))
                        : (activateLabel ?? t('activation.activate', 'Activate Container Engine'))}
                </Button>
            </DialogBox>
        );
    }

    return <>{children}</>;
}
